import { useTranslation } from 'react-i18next';
import { LuMapPin, LuCalendar, LuFileText, LuFileSpreadsheet, LuFile, LuUser, LuVideo } from 'react-icons/lu';
import { FiAlertTriangle } from 'react-icons/fi';
import { format } from 'date-fns';

const priorityColors = {
  Low: 'bg-gray-100 text-gray-700',
  Medium: 'bg-blue-100 text-blue-700',
  High: 'bg-orange-100 text-orange-700',
  Urgent: 'bg-red-100 text-red-700'
};

const statusColors = {
  New: 'bg-purple-100 text-purple-700',
  Acknowledged: 'bg-blue-100 text-blue-700',
  InProgress: 'bg-yellow-100 text-yellow-700',
  Waiting: 'bg-gray-100 text-gray-700',
  Resolved: 'bg-green-100 text-green-700',
  Closed: 'bg-gray-100 text-gray-500'
};

export default function FaultReportCard({ fault, onClick }) {
  const { t } = useTranslation();

  const getFileType = (item) => {
    if (!item) return 'file';
    
    // Handle both object with file_url or direct string URL
    const url = typeof item === 'string' ? item : (item.file_url || '');
    const name = typeof item === 'object' ? (item.document_name || '') : '';
    
    if (!url) return 'file';

    let ext = '';
    
    // 1. Try to get extension from document_name first (most reliable)
    if (name && name.includes('.')) {
      ext = name.split('.').pop()?.toLowerCase();
    }
    
    // 2. Fallback to URL if no valid extension found in name
    if (!ext) {
      const cleanUrl = url.split(/[?#]/)[0]; // Remove query params
      ext = cleanUrl.split('.').pop()?.toLowerCase();
    }
    
    if (['jpg', 'jpeg', 'png', 'webp', 'avif', 'gif', 'svg', 'heic', 'heif', 'bmp', 'tiff'].includes(ext)) return 'image';
    if (['mp4', 'mov', 'webm', 'ogg', 'avi', 'mkv', 'm4v', '3gp', 'qt'].includes(ext)) return 'video';
    if (['xls', 'xlsx', 'csv', 'numbers', 'ods'].includes(ext)) return 'spreadsheet';
    if (['pdf', 'doc', 'docx', 'txt', 'rtf', 'odt', 'pages'].includes(ext)) return 'document';
    
    return 'file';
  };

  // Find the first image to use as thumbnail
  const imageAttachment = fault.photos?.find(photo => getFileType(photo) === 'image');
  
  // Use the found image, or fallback to the very first attachment if no image exists
  const displayAttachment = imageAttachment || fault.photos?.[0];
  const displayUrl = displayAttachment?.file_url;

  const renderThumbnail = () => {
    if (!displayUrl) {
      return (
        <div className="w-full h-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
          <FiAlertTriangle className="w-12 h-12 text-blue-200" />
        </div>
      );
    }

    const type = getFileType(displayAttachment);

    switch (type) {
      case 'image':
        return (
          <img
            src={displayUrl}
            alt={fault.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        );
      case 'video':
        return (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 gap-2">
            <LuVideo className="w-12 h-12 text-gray-400" />
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">VIDEO</span>
          </div>
        );
      case 'spreadsheet':
        return (
          <div className="w-full h-full flex flex-col items-center justify-center bg-green-50 gap-2">
            <LuFileSpreadsheet className="w-12 h-12 text-green-300" />
            <span className="text-[10px] font-bold text-green-500 uppercase tracking-widest">{t('faultReports.excelAttachment')}</span>
          </div>
        );
      case 'document':
        return (
          <div className="w-full h-full flex flex-col items-center justify-center bg-orange-50 gap-2">
            <LuFileText className="w-12 h-12 text-orange-300" />
            <span className="text-[10px] font-bold text-orange-500 uppercase tracking-widest">{t('faultReports.document')}</span>
          </div>
        );
      default:
        return (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 gap-2">
            <LuFile className="w-12 h-12 text-gray-300" />
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t('faultReports.attachment')}</span>
          </div>
        );
    }
  };

  const reporterName = fault.is_anonymous ? t('faultReports.anonymous') : fault.reporter_name || t('faultReports.unknownUser');

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-[2rem] border border-gray-100 hover:shadow-2xl hover:shadow-gray-200 transition-all cursor-pointer overflow-hidden group"
    >
      <div className="h-48 relative overflow-hidden bg-gray-50">
        {renderThumbnail()}
      </div>
      
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${priorityColors[fault.priority]}`}>
            {t(`faultReports.priority${fault.priority}`)}
          </span>
          <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${statusColors[fault.status]}`}>
            {t(`faultReports.status${fault.status.replace(' ', '')}`)}
          </span>
        </div>

        <div>
          <h3 className="text-lg font-bold text-gray-900 line-clamp-1 group-hover:text-blue-600 transition-colors">
            {fault.title}
          </h3>
          <p className="text-xs font-medium text-gray-400 uppercase tracking-widest mt-1">
            {t(`faultReports.category${fault.category}`)}
          </p>
        </div>

        <div className="space-y-3 pt-2 border-t border-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-500 font-medium">
              <LuUser className="w-4 h-4 text-blue-400" />
              <span className="truncate max-w-[120px]">{reporterName}</span>
            </div>
            <div className="flex items-center gap-2 text-[11px] text-gray-400 font-bold uppercase tracking-tighter">
              <LuCalendar className="w-3.5 h-3.5" />
              <span>{format(new Date(fault.created_at), 'MMM d, yyyy')}</span>
            </div>
          </div>

          {fault.location && (
            <div className="flex items-center gap-2 text-sm text-gray-500 font-medium">
              <LuMapPin className="w-4 h-4 text-gray-400" />
              <span className="truncate">{fault.location}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}